import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import MarketingBackdrop from "../../components/MarketingBackdrop";
import SEO from "../../components/SEO";
import { listMarketplaceAdvisors } from "../../api/marketplaceApi";

// Public-facing discovery page. Available to logged-out visitors too, so
// they can browse advisors before committing to a sign-up.

const JURISDICTIONS = [
  { value: "",   label: "All regions" },
  { value: "IN", label: "🇮🇳 India"          },
  { value: "US", label: "🇺🇸 United States"  },
  { value: "GB", label: "🇬🇧 United Kingdom" },
  { value: "CA", label: "🇨🇦 Canada"         },
  { value: "AU", label: "🇦🇺 Australia"      },
  { value: "SG", label: "🇸🇬 Singapore"      },
  { value: "AE", label: "🇦🇪 UAE"            },
];

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const AdvisorAvatar = ({ src, name, size = 56 }) => (
  <div
    style={{ width: size, height: size }}
    className="rounded-full overflow-hidden flex-shrink-0 bg-indigo-600 flex items-center justify-center font-bold text-white"
  >
    {src ? (
      <img src={src} alt={name} className="w-full h-full object-cover" />
    ) : (
      <span style={{ fontSize: size * 0.4 }}>
        {(name || "?")
          .split(" ")
          .map((s) => s[0])
          .filter(Boolean)
          .slice(0, 2)
          .join("")
          .toUpperCase()}
      </span>
    )}
  </div>
);

const AdvisorCard = ({ advisor }) => {
  const quals = (advisor.qualifications || []).slice(0, 3);
  return (
    <Link
      to={`/marketplace/advisor/${advisor.slug}`}
      className="group block p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 hover:border-indigo-400 dark:hover:border-indigo-500/60 transition-colors shadow-glass dark:shadow-glass-dark"
    >
      <div className="flex items-start gap-4">
        <AdvisorAvatar src={advisor.headshot_url} name={advisor.display_name} size={56} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-lg truncate">{advisor.display_name}</h3>
            {advisor.jurisdiction && (
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-500">
                {advisor.jurisdiction}
              </span>
            )}
          </div>
          {advisor.headline && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {advisor.headline}
            </p>
          )}
          {quals.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {quals.map((q, i) => (
                <span
                  key={i}
                  className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500"
                >
                  {q.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const Marketplace = () => {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jurisdiction, setJurisdiction] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setAdvisors(await listMarketplaceAdvisors({ jurisdiction }));
      } finally {
        setLoading(false);
      }
    })();
  }, [jurisdiction]);

  return (
    <div className="min-h-screen pt-28 bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white relative">
      <SEO
        path="/marketplace"
        title="Advisor Marketplace"
        description="Browse FinAI's marketplace of financial advisors and AI skill plans. Discover specialists by region and subscribe to sharpen your AI council."
      />
      <MarketingBackdrop />
      <Navbar />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.02em]">
              Advisor marketplace
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2 max-w-2xl">
              Subscribe to a strategy. The AI agents apply it to your portfolio in
              recommend-only mode — you approve every action. Advisors never see who
              you are.
            </p>
          </div>
          <div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Region
              </span>
              <select
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {JURISDICTIONS.map((j) => (
                  <option key={j.value || "_all"} value={j.value}>{j.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <Card>Loading advisors…</Card>
        ) : advisors.length === 0 ? (
          <Card>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No advisors are listed yet for this region. Check back soon.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {advisors.map((a) => (
              <AdvisorCard key={a.slug} advisor={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
